/**
 * The owner's *declared* business line — where it is stored, and who may claim
 * it (doc 18 D2).
 *
 * Lives in `@acs/shared` because three runtimes have to agree on the answer and
 * they see completely different data: the portal checks a live list already in
 * memory, `declareSourceNumber` checks a Firestore query, and the gateway
 * matches an inbound call against what was stored. A rule copied into each of
 * them is a rule that drifts — and the failure mode of drift here is a business
 * that passes the portal's check, is rejected by the server, or worse, is
 * accepted by both and then never receives a call.
 */
/**
 * The declared line, wherever it currently lives.
 *
 * It moves: declared before provisioning it sits in `pendingSourceNumber`, and
 * provisioning drains it into `forwardNumber.sourceNumber`. Callers care that a
 * line was declared, not which field holds it today — and code that read only
 * the latter has twice answered "not declared" for a business that had in fact
 * declared one.
 */
export function declaredSourceOf(business) {
    return business.forwardNumber?.sourceNumber ?? business.pendingSourceNumber ?? null;
}
/**
 * Is `canonical` (E.164) already spoken for by this business?
 *
 * Archived businesses claim nothing: their numbers are released and their
 * routing grants are gone, so holding their old line hostage would stop an
 * owner from re-adding a business they closed and reopened.
 *
 * Compares canonical E.164 on both sides — never raw input. `+48 512 345 678`
 * and `0048512345678` are the same line, and a string compare would let both be
 * declared, producing exactly the collision this check exists to prevent.
 */
export function claimsSourceNumber(business, canonical) {
    if (business.status === 'archived')
        return false;
    return declaredSourceOf(business) === canonical;
}
//# sourceMappingURL=sourceNumber.js.map